import React, { useEffect, useRef, useState } from "react";
import classnames from "classnames";

// Components
import Input from "@JohnLewisPartnership/jl-design-system/dist/elements/input/Input";
import { SearchOutlined as SearchIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/SearchOutlined";
import SuggestionsContainer from "./suggestions-container/SuggestionsContainer";

// Styles
import styles from "./faq-search-form.module.scss";

function FAQSearchForm({
  onSubmit,
  onSearchChange = () => {},
  onSuggestionSelected = () => {},
  query = "",
  minStrLength = 3,
  suggestions = [],
  suggestionsSize = 5,
  setQuery,
  inContactUs = false,
  setSolutionId,
  showTitle = true,
}) {
  const inputEl = useRef(null);
  const [searchStr, setSearchStr] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const title = inContactUs ? "Got a question? Search for help topics" : "Frequently asked questions";

  useEffect(() => {
    const checkIfClickOutside = evt => {
      if (evt.path && !evt.path.some(p => p.id && p.id.includes("faq-search-wrapper"))) {
        setShowSuggestions(false);
      }
    };

    window.addEventListener("click", checkIfClickOutside);

    return () => {
      window.removeEventListener("click", checkIfClickOutside);
    };
  }, []);

  const handleSubmit = event => {
    if (inputEl.current) inputEl.current.input.blur();
    onSubmit(event);
    if (inContactUs) {
      setSolutionId("");
    }
  };

  const handleInputChange = e => {
    setShowSuggestions(true);
    setSearchStr(e.target.value);
    onSearchChange(e.target.value.length >= minStrLength ? e.target.value : null);

    setQuery(e.target.value);
  };

  const handleKeyDown = e => {
    const { keyCode } = e;
    const ARROW_DOWN = 40;
    const ARROW_UP = 38;
    const ENTER = 13;
    const TAB = 9;

    if (keyCode === TAB) {
      setShowSuggestions(false);
    }

    if (keyCode === ENTER) {
      if (selectedSuggestion !== null) {
        onSuggestionSelected(suggestions.slice(0, suggestionsSize)[selectedSuggestion]);
      }
    }

    if (keyCode === ARROW_DOWN) {
      if (!(selectedSuggestion >= suggestionsSize - 1)) {
        setSelectedSuggestion(selectedSuggestion === null ? 0 : selectedSuggestion + 1);
      }
    }

    if (keyCode === ARROW_UP) {
      if (selectedSuggestion !== null) {
        setSelectedSuggestion(selectedSuggestion === 0 ? null : selectedSuggestion - 1);
      }
    }
  };

  return (
    <form action="/faq" onSubmit={handleSubmit} className={styles.faqForm}>
      {showTitle &&
        (inContactUs ? (
          <h2 className={styles.formTitleContactUs}>{title}</h2>
        ) : (
          <h1 className={classnames("util-sectionHeading", styles.faqForm__title)}>{title}</h1>
        ))}
      <div id="faq-search-wrapper" className={styles.faqForm__inputWrapper}>
        <label htmlFor="search" className="util-visuallyHiddenText">
          Editable search bar. Start typing to get suggested searches.
        </label>
        <Input
          id="search"
          name="q"
          className={styles.faqForm__input}
          value={query}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          placeholder="Search keywords or a question"
          ref={inputEl}
          autoComplete="off"
          aria-activedescendant="popularSearches-dell-inspiron-13-5000"
          extensionProps={{
            onKeyDown: handleKeyDown,
          }}
        />
        <button className={styles.faqForm__button} type="submit" name="searchButton">
          <div className={styles.faqForm__iconWrapper}>
            <SearchIcon title="Search" />
          </div>
        </button>
        {showSuggestions && suggestions.length ? (
          <SuggestionsContainer
            suggestions={suggestions.slice(0, suggestionsSize)}
            searchStr={searchStr}
            selectedSuggestion={selectedSuggestion}
            onSuggestionSelected={onSuggestionSelected}
          />
        ) : null}
      </div>
    </form>
  );
}

export default FAQSearchForm;
